import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { concatMap, debounceTime, distinctUntilChanged, EMPTY, filter, forkJoin, from, map, Observable, of, Subject, Subscription, switchMap, takeUntil, tap, toArray } from 'rxjs';
import { ProjectTeamServiceService } from '../services/project-team-service.service';
import { DataManipulationService } from 'src/app/services/data-manipulation.service';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import { ModalService } from 'src/app/services/modal.service';
import { GenericConfirmModalComponent } from 'src/app/components/modals/generic-confirm-modal/generic-confirm-modal.component';
import { ProjectTeamDrillDownModalComponent } from 'src/app/components/modals/project-team-drill-down-modal/project-team-drill-down-modal.component';
import { UpdatePhaseModalComponent } from 'src/app/components/modals/update-phase-modal/update-phase-modal.component';
import { ProjectTeamAddNoteModalComponent } from 'src/app/components/modals/project-team-add-note-modal/project-team-add-note-modal.component';
import { ChangeProjectFrequencyComponent } from 'src/app/components/modals/change-project-frequency/change-project-frequency.component';

Chart.register(...registerables, annotationPlugin);

interface ProjectTeam {
  uuid: string,
  name: string,
  phase: any,
  leaders: any,
  members: any,
  projectHighlights: any,
  projectStatistics: any,
  projectChartData: any,
  projectAssigments: any,
  collections: any,
  status: {
    name: string,
    title: string,
    color: string
  },
  kickOffDate: any,
  startDate: any,
  endDate: any,
  projectFrequency: any,
  remainingDays: any,
  activeProject: any,
  projectType: any
  currentCheckin: any
}

interface Checkin {
  uuid: string,
  name: string;
  description: any,
  score: number;
  exists: boolean;
  responseRate: number;
  startDate: string;
  endDate: string | null;
  phaseTitle: string;
  showNote: boolean
}

interface PhaseAnnotation {
  xMin: string;
  xMax: string;
  title: string;
}

@Component({
  selector: 'app-component-project-team-dashboard',
  templateUrl: './component-project-team-dashboard.component.html',
  styleUrls: ['./component-project-team-dashboard.component.scss']
})
export class ComponentProjectTeamDashboardComponent implements OnInit, OnDestroy {

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChildren('accordionDiv') accordionDivs! : QueryList<ElementRef>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if ((this.showEditTeam || this.showEditTeamFromStatus) && !this.removeMemberModalOpen) {
      this.showEditTeam = false;
      this.showEditTeamFromStatus = false;
      this.showBlinkEffect = false;
      this.showUserSearchContainer = false;
    }
  }

  chart!: Chart;
  processedPoints: Checkin[] = [];
  annotations: any = {};
  noteIconHitboxes: any[] = [];
  hoveredIconIndex: number | null = null;

  projectTeamUuid: any;
  previousShowSelectedTabCategIndex: any;
  showSelectedTabCateg: any;
  previousShowSelectedTabStateIndex: any;
  responseDistribution: any;
  deadlineModalDate: any;
  showedOpenQsResponse: any;
  userProjectMembership: any;

  statementResponseCount: number = 0;
  statementResponsePage: number = 0;

  scoreColor: string = '';
  responseRateBackgroundColor: string = '';

  projectTeam: ProjectTeam = {
    uuid: '',
    name: '',
    phase: {},
    leaders: [],
    members: [],
    projectHighlights: [],
    projectStatistics: {},
    projectChartData: [],
    projectAssigments: [],
    collections: [],
    status: {
      name: '',
      title: '',
      color: ''
    },
    kickOffDate: {},
    startDate: {},
    endDate: {},
    projectFrequency: {},
    remainingDays: {},
    activeProject: {},
    projectType: {},
    currentCheckin: {}
  };

  chartOptions = {
    plugins: {
      tooltip: {
        enabled: false
      },

    },
    elements : {
      arc : {
        borderColor: '#E6E6E6',
        borderWidth: 3,
      }
    }
  }

  showDashboard: boolean = false;
  showStatus: boolean = false;
  showEditTeam: boolean = false;
  showEditTeamFromStatus: boolean = false;
  showBlinkEffect: boolean = false;
  userDropdownVisible: boolean = false;
  showUserSearchContainer: boolean = false;
  chartHaveData: boolean = false;
  showResponseModal: boolean = false;
  removeMemberModalOpen: boolean = false;
  isHighlightScroll: boolean = false;

  indicatorInterval: any[] = [];
  chartData: any[] = [];
  statementResponses: any[] = [];
  showSelectedTabState: any[] = [];
  selectedUsers: any[] = [];
  pickedUsersForProject: any[] = [];
  chartPhaseColors: any[] = [
    {annotationBackground: 'rgba(255, 252, 243, 1)', labelBackground: 'rgba(255, 229, 154, 1)'},
    {annotationBackground: 'rgba(255, 248, 253, 1)', labelBackground: 'rgba(252, 187, 235, 1)'},
    {annotationBackground: 'rgba(247, 255, 254, 1)', labelBackground: 'rgba(187, 252, 247, 1)'}
  ];
  indexToScrollTo: any[] = [];

  workspaceProjects$!: Observable<any[]>;

  private intervalsSub!: Subscription;

  usersSearchControl = new FormControl('');

  constructor(
    private activeRoute: ActivatedRoute,
    private projectService: ProjectTeamServiceService,
    public data: DataManipulationService,
    public translate: TranslateService,
    private datePipe: DatePipe,
    private modalService: ModalService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnDestroy(): void {
    this.intervalsSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.activeRoute.paramMap.subscribe((params: any) => {
      this.showDashboard = false;
      this.chartData = [];
      this.projectTeam.projectChartData = [];
      this.projectTeam.projectStatistics = null;
      this.projectTeamUuid = params.get('projectUuid');
      this.getProjectData();
      this.data.showLoading();
    });

    this.usersSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: any) => {
        if (query !== '') {
          this.userDropdownVisible = true;
          return this.projectService.searchUser(query);
        } else {
          this.pickedUsersForProject = [];
          return of(null);
        }
      })
    ).subscribe((users: any) => {
      if (users) {
        const selectedUsers = new Set([
          ...this.selectedUsers.map((sUser: any) => sUser.user.uuid),
          ...this.projectTeam.members.map((member: any) => member.user.uuid),
          ...this.projectTeam.leaders.map((leader: any) => leader.user.uuid)
        ]);
        this.pickedUsersForProject = users.filter((user: any) => !selectedUsers.has(user.user.uuid));
      }
    });
  }

  private prepareData(): void {
    let globalLastValidScore: number | null = null;
    let globalLastValidEndDate: string | null = null;
    let phaseAnnotations: PhaseAnnotation[] = [];
    let allPoints: Checkin[] = [];
    this.projectTeam.projectChartData.forEach((phaseObj: any, index: number) => {
      const phase = phaseObj.phase;
      const chartData = phase.chartData;

      let lastValidEndDate: string | null = globalLastValidEndDate;
      let lastValidScore: number | null = globalLastValidScore;

      const phasePoints = chartData.map((p: any) => {
        const end = p.endDate?.date;
        if (end && end !== '-0001-11-30 00:00:00.000000') {
          lastValidEndDate = end;
          globalLastValidEndDate = end;
        }
        let scoreValue = p.score;
        if (scoreValue !== 0 && p.exists) {
          lastValidScore = scoreValue;
          globalLastValidScore = scoreValue;
        } else {
          scoreValue = lastValidScore ?? 0;
        }

        return {
          uuid: p.uuid,
          name: p.name,
          description: p.description,
          score: scoreValue,
          exists: p.exists,
          responseRate: p.responseRate,
          startDate: p.startDate?.date,
          endDate: end,
          phaseTitle: phase.title,
          phaseViewOrder: phase.viewOrder,
          showNote: !!p.description
        };
      }).filter((p: Checkin) => !!p.endDate);
      if (phasePoints.length) {
        let xMax: string;
        const xMin = phasePoints[0].endDate!;
        if (this.projectTeam.projectChartData.length > 1 && ((index + 1) < this.projectTeam.projectChartData.length)) {
          xMax = this.projectTeam.projectChartData[index + 1].phase.chartData[0].endDate.date;
        } else {
          xMax = phasePoints[phasePoints.length - 1].endDate!;
        }
        phaseAnnotations.push({ xMin, xMax, title: phase.title });
      }

      allPoints = [...allPoints, ...phasePoints];
    });
    this.processedPoints = allPoints.sort(
      (a, b) => new Date(a.endDate!).getTime() - new Date(b.endDate!).getTime()
    );
    this.annotations = {};
    phaseAnnotations.forEach((ph, idx) => {
      this.annotations[`phase${idx}`] = {
        type: 'box',
        xMin: ph.xMin,
        xMax: ph.xMax,
        yMin: 0,
        yMax: 100,
        backgroundColor: this.chartPhaseColors[idx].annotationBackground,
        borderWidth: 0,
        drawTime: 'beforeDraw',
        labelContent: `Phase: ${ph.title}`,
        labelFont: {size: 14, weight: '400'},
        labelColor: '#000000',
        labelBgColor: this.chartPhaseColors[idx].labelBackground
      };
    });

    const phaseLabelPlugin = {
      id: 'phaseLabels',
      afterDatasetsDraw(chart: any) {
        const ctx = chart.ctx;
        const annotations = chart.options.plugins.annotation.annotations;

        Object.values(annotations).forEach((ann: any) => {
          if (ann.type === 'box' && ann.labelContent) {

            const xStart = chart.scales.x.getPixelForValue(new Date(ann.xMin));
            const yEnd = chart.scales.y.getPixelForValue(100);

            const padding = 6;
            const radius = 6;
            ctx.save();

            ctx.font = `${ann.labelFont.weight} ${ann.labelFont.size}px sans-serif`;
            ctx.textBaseline = 'top';
            const textWidth = ctx.measureText(ann.labelContent).width;

            const rectX = xStart + 10;
            const rectY = yEnd - ann.labelFont.size + 20;
            const rectWidth = textWidth + padding * 2;
            const rectHeight = ann.labelFont.size + padding * 2;

            ctx.fillStyle = ann.labelBgColor;
            roundRect(ctx, rectX, rectY, rectWidth, rectHeight, radius);
            ctx.fill();

            ctx.fillStyle = ann.labelColor;
            ctx.textAlign = 'left';
            ctx.fillText(ann.labelContent, rectX + padding, rectY + padding);

            ctx.restore();
          }
        });

        function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
          ctx.beginPath();
          ctx.moveTo(x + r, y);
          ctx.lineTo(x + w - r, y);
          ctx.quadraticCurveTo(x + w, y, x + w, y + r);
          ctx.lineTo(x + w, y + h - r);
          ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
          ctx.lineTo(x + r, y + h);
          ctx.quadraticCurveTo(x, y + h, x, y + h - r);
          ctx.lineTo(x, y + r);
          ctx.quadraticCurveTo(x, y, x + r, y);
          ctx.closePath();
        }
      }
    };
    Chart.register(phaseLabelPlugin);
  }

  private getNotePointsPlugin() {
    const img = new Image();
    img.src = '../../../../assets/svg/see_add_note.svg';

    return {
      id: 'pointIcons',

      afterDatasetsDraw: (chart: any) => {
        const { ctx } = chart;
        this.noteIconHitboxes = [];
        const meta = chart.getDatasetMeta(0);
        meta.data.forEach((point: any, index: number) => {
          const isHovered = this.hoveredIconIndex === index;
          const dataPoint = this.processedPoints[index];
          if (!dataPoint?.showNote) {
            return;
          }
          const x = point.x;
          const y = point.y - 23;
          const radius = 10;
          ctx.save();
          ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
          ctx.showBlur = 8;
          ctx.beginPath();
          ctx.arc(x, y, 10, 0, Math.PI * 2);
          ctx.fillStyle = '#DDE9FF';
          ctx.fill();
          ctx.lineWidth = isHovered ? 2 : 1;
          ctx.strokeStyle = '#A0ADCE';
          ctx.stroke();
          ctx.fillStyle = '#FFFFFF';
          ctx.font = '12px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          ctx.drawImage(img, x - 7, y - 7, 14, 14);

          ctx.restore();
          this.noteIconHitboxes.push({x, y, radius, point: dataPoint, index});
        });
      }
    };
  }

  private renderChart(): void {
    const labels = this.processedPoints.map(p => p.endDate);
    const pointColors = this.processedPoints.map(p => p.exists ? '#FFFFFF' : '#FFFFFF');
    const pointBorderColors = this.processedPoints.map(p => p.exists ? '#2551BF' : '#A0ADCE');

    const segment = {
      borderDash: (ctx: any) => {
        const current = this.processedPoints[ctx.p0DataIndex];
        const next = this.processedPoints[ctx.p1DataIndex];

        if (current.exists && next.exists) {
          return [];
        }
        return [6, 6];
      },
      borderColor: (ctx: any) => {
        const current = this.processedPoints[ctx.p0DataIndex];
        const next = this.processedPoints[ctx.p1DataIndex];

        return (current.exists && next.exists) ? '#3b82f6' : '#d1d5db';
      }
    };
    const tooltip = {
      enabled: false,
      external: (context: any) => {
        let tooltipEl = document.getElementById('chartjs-tooltip');
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'chartjs-tooltip';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.background = '#E4EDFE';
          tooltipEl.style.border = '1px solid #D3E0F9';
          tooltipEl.style.borderRadius = '6px';
          tooltipEl.style.padding = '8px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
          tooltipEl.style.fontSize = '13px';
          tooltipEl.style.color = '#000000';
          tooltipEl.style.zIndex = '100';
          tooltipEl.style.left = '0px';
          tooltipEl.style.top = '0px';
          tooltipEl.innerHTML = '<div></div>';
          document.body.appendChild(tooltipEl);
        }

        const tooltipModel = context.tooltip;
        if (!tooltipModel || tooltipModel.opacity === 0) {
          tooltipEl.style.opacity = '0';
          return;
        }

        const index = tooltipModel.dataPoints[0].dataIndex;
        const point = this.processedPoints[index];
        const start = point.startDate?.split(' ')[0];
        const end = point.endDate?.split(' ')[0];
        const tooltipWidth = tooltipEl.offsetWidth;

        const scoreSection = point.exists ? `
          <div style="display: flex; align-items: center; gap: 6px;">
            <img src="https://img.icons8.com/ios-filled/16/graph.png" />
            <span style="font-weight:500">Score: ${point.score}</span>
          </div>
        ` : '';

        if (end) {
          tooltipEl.querySelector('div')!.innerHTML = `
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <div style="margin-bottom: 1rem; font-weight:500">
                  <span>${point.name}</span>
              </div>
              ${scoreSection}
              <div style="display: flex; align-items: center; gap: 6px;">
                <img src="https://img.icons8.com/ios-filled/16/calendar.png"/>
                <span style="font-weight:500">${this.datePipe.transform(new Date(start), 'dd MMM')} - ${this.datePipe.transform(new Date(end), 'dd MMM')}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 6px;">
                <img style="width: 14px; height: 14px" src="../../../../assets/svg/project-team-response-rate-icon.svg">
                <span style="font-weight:500">Response rate: ${point.responseRate}%</span>
              </div>
            </div>
          `;
        }

        const canvasRect = context.chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = '1';
        tooltipEl.style.left = canvasRect.left + window.scrollX + tooltipModel.caretX - tooltipWidth - 10 + 'px';
        tooltipEl.style.top = canvasRect.top + window.scrollY + tooltipModel.caretY + 'px';
      }
    };

    const canvas = this.chartCanvas.nativeElement;
    if (!canvas) {
      console.log('No canvas');
      return;
    }
    const ctx = canvas;
    const notePointsPlugin = this.getNotePointsPlugin();
    this.chart = new Chart(ctx, {
      type: 'line',
      plugins: [
        notePointsPlugin
      ],
      data: {
        labels,
        datasets: [
          {
            data: this.processedPoints.map(p => p.score),
            pointBackgroundColor: pointColors,
            pointBorderColor: pointBorderColors,
            pointBorderWidth: 3,
            pointRadius: 8,
            pointHoverRadius: 8,
            pointHoverBorderWidth: 3,
            pointStyle: 'circle',
            fill: false,
            segment,
            tension: 0.3,
            clip: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              parser: 'yyyy-MM-dd HH:mm:ss.SSSSSS',
              unit: 'day',
              tooltipFormat: 'MMM dd'
            },
            title: {
              display: true,
              text: ''
            },
            ticks: {
              source: 'data',
              autoSkip: true,
              maxTicksLimit: Math.ceil(this.processedPoints.length / 2),
              maxRotation: 0,
              minRotation: 0
            },
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20
            }
          }
        },
        plugins: {
          tooltip,
          annotation: {
            annotations: this.annotations
          },
          legend: { display: false }
        },
        onClick: (event: any) => {
          const canvas = this.chart.canvas;
          const rect = canvas.getBoundingClientRect();

          const mouseX = event.native.clientX - rect.left;
          const mouseY = event.native.clientY - rect.top;

          const clickedIcon = this.noteIconHitboxes.find((icon: any) => {
            const dx = mouseX - icon.x;
            const dy = mouseY - icon.y;

            return Math.sqrt(dx * dx + dy * dy) <= icon.radius;
          });

          if (clickedIcon) {
            const checkinToOpenNote = {
              uuid: clickedIcon.point.uuid,
              description: clickedIcon.point.description,
              questionCollection : {
                title: clickedIcon.point.name
              }
            }
            this.openAddNoteModal(checkinToOpenNote);
          }
        },
        onHover: (event: any) => {
          const canvas = this.chart.canvas;
          const rect = canvas.getBoundingClientRect();

          const mouseX = event.native.clientX - rect.left;
          const mouseY = event.native.clientY - rect.top;

          const hoveredIcon = this.noteIconHitboxes.find((icon: any) => {
            const dx = mouseX - icon.x;
            const dy = mouseY - icon.y;

            return Math.sqrt(dx * dx + dy * dy) <= icon.radius;
          });

          canvas.style.cursor = hoveredIcon
            ? 'pointer'
            : 'default';
          const newHoveredIndex = hoveredIcon?.index ?? null;
          if (this.hoveredIconIndex !== newHoveredIndex) {
            this.hoveredIconIndex = newHoveredIndex;
            this.chart.draw();
          }
        }
      }
    });
  }

  getLinkParams() {
    this.projectTeamUuid = this.activeRoute.snapshot.paramMap.get('projectUuid');
  }

  getProjectData() {
    of(null).pipe(
      concatMap(() => this.getIndicatorInterval()),
      concatMap((indicatorIntervals: any) => {
        const projectTeamStatistics$ = this.getProjectStatisticsAndProcessData().pipe(
          map((result:any) => result)
        );
        const projectChartData$ = this.processProjectTeamChartData().pipe(
          map((result: any) => result)
        );
        const projectHighlights$ = this.projectService.getProjectTeamHighlights(this.projectTeamUuid).pipe(
          map((res: any) => res._embedded.highlights || [])
        );
        const projectMembers$ = this.getProjectTeamMembersAndFilterThem().pipe(
          map((result:any) => result)
        );
        const activeProject$ = this.projectService.getActiveProjectDetails(this.projectTeamUuid).pipe(
          map((result: any) => result)
        );
        const projectCollections$ = this.getQuestionCollectionsScore().pipe(
          map((result: any) => result)
        );
        const projectAssigments$ = this.projectService.getProjectTeamAssignments(this.projectTeamUuid).pipe(
          map((res: any) => res._embedded.assignments || [])
        );
        return forkJoin({
          indicatorIntervals: of(indicatorIntervals),
          projectTeamStatistics: projectTeamStatistics$,
          projectChartData: projectChartData$,
          projectCollections: projectCollections$,
          projectHighlights: projectHighlights$,
          projectMembers: projectMembers$,
          activeProject: activeProject$,
          projectAssigments: projectAssigments$
        })
      })
    ).subscribe((res:any) => {
      this.data.workspaceProjects$.pipe(
        map(arr => arr.find((item: any) => item.uuid === this.projectTeamUuid) ?? null)
      ).subscribe((value: any) => {
        if (value) {
          this.projectTeam.uuid = value.uuid;
          this.projectTeam.name = value.name;
          this.projectTeam.phase = value.phase;
          this.projectTeam.kickOffDate = value.kickOffMeetingDate;
          this.projectTeam.startDate = value.startDate;
          this.projectTeam.endDate = value.endDate;
          this.projectTeam.projectFrequency = value.projectFrequency;
          this.projectTeam.status.name = value.status;
          this.projectTeam.projectType = value.projectType;
          this.deadlineModalDate = new Date(value.endDate.date);
        }
      });
      this.indicatorInterval = res.indicatorIntervals;
      this.projectTeam.projectStatistics = res.projectTeamStatistics;
      this.projectTeam.projectChartData = res.projectChartData;
      this.projectTeam.projectHighlights = res.projectHighlights;
      this.projectTeam.projectAssigments = res.projectAssigments;
      this.projectTeam.leaders = res.projectMembers.leaders;
      this.projectTeam.members = res.projectMembers.members;
      this.projectTeam.collections = res.projectCollections;
      this.projectTeam.currentCheckin = res.projectCollections[0] ?? null;
      this.projectTeam.activeProject = res.activeProject;
      this.showSelectedTabCateg = this.projectTeam.collections.map(() => false);
      this.indexToScrollTo = this.projectTeam.collections.map(() => null);
      this.getProjectTeamDaysRemaining(new Date(), new Date(this.projectTeam.endDate.date));
      this.getProjectTeamStatus();
      this.getProjectTeamScoreColor();
      this.getResponseRateColor();
      this.setActiveProjectStatus();
      this.filterProjectTeamChartDataAndShowChart();
      if (this.projectTeam.projectAssigments.length !== 0) this.getAssignmentsTitleAndDescription();
      this.data.hideLoading();
      this.showDashboard = true;
      setTimeout(() => this.renderChart());
    });
  }

  getIndicatorInterval(): Observable<any[]> {
    return this.projectService.getIndicatorInterval().pipe(
      map((res: any) => {
        this.indicatorInterval = [...res]
        return this.indicatorInterval;
      })
    );
  }

  get projectRating() {
    return Math.round(this.projectTeam.projectStatistics.score/10);
  }

  get limitedMembers() {
    if (this.projectTeam.members.length <= 5) {
      return this.projectTeam.members;
    } else {
      return (this.projectTeam.members ?? []).slice(0, 5);
    }
  }

  filterProjectTeamChartDataAndShowChart() {
    const existingPoints = this.projectTeam.projectChartData.map((phase:any) => {
      return phase.phase.chartData.filter((data:any) => data.exists);
    }).length;
    if (existingPoints !== 0) {
      if (this.chart) {
        this.chart.destroy();
      }
      this.chartHaveData = true;
      this.prepareData();
    }
  }

  getAssignmentsTitleAndDescription() {
    const TWO_DAYS = 2 * 24 * 60 * 60 * 1000;
    const indices = [0, 1, 2].sort(() => Math.random() - 0.5);
    let assignmentIndex: number = 0;
    this.translate.get('project_team_assignments').subscribe((res: any) => {
      this.projectTeam.projectAssigments.forEach((assignment: any, index: number) => {
        let randomIndex = Math.floor(Math.random() * (res.length - 1));
        if ((new Date(assignment.dueDate.date).getTime() - Date.now()) <= TWO_DAYS) {
          assignmentIndex = 3;
        } else {
          assignmentIndex = indices[randomIndex];
        }
        assignment.title = res[assignmentIndex].title;
        assignment.description = res[assignmentIndex].description;
      });
    });
  }

  goToProjectFeedbackPage(projectAssignmentUuid: any) {
    const url = 'https://qa.successteam.com/projectTeam?projectAssignmentUuid='+projectAssignmentUuid;
    window.location.href = url;
  }

  getQuestionCollectionsScore(): Observable<any[]> {
    return this.projectService.getProjectTeamTimedQuestionCollections(this.projectTeamUuid).pipe(
      concatMap(objects =>
        from(objects._embedded.collections).pipe(
          map((collection: any) => {
            const today = new Date();
            const endDate = new Date(collection.endDate.date);
            if (collection.score !== 0) {
              const matchedInterval = this.indicatorInterval.find(interval => interval.min <= collection.score && collection.score <= interval.max);
              collection.color = matchedInterval.indicatorColor.color;
              this.chartData.push({
                datasets: [{
                  data: [collection.score,(100 - collection.score)],
                  backgroundColor: [
                    //@ts-ignore
                    this.data.colors[''+matchedInterval.indicatorColor.color], "#E6E6E6"
                  ],
                }]
              });
            } else {
              collection.color = 'blue';
              this.chartData.push({
                datasets: [{
                  data: [100,0],
                  backgroundColor: [
                    "#E6E6E6"
                  ],
                }]
              });
            }
            return {...collection, finishedCheckin: endDate <= today};
          }),
          toArray()
        )
      )
    );
  }

  getProjectStatisticsAndProcessData(): Observable<any> {
    return this.projectService.getProjectTeamStatistics(this.projectTeamUuid).pipe(
      map((source: any) => {
        const categories = Object.entries(source)
          .filter(([key]) => key !== 'responseRate' && key !== 'score' && key !== 'eligibleForResult' && key !== 'previousResponseRate' && key !== 'previousScore')
          .map(([key, value]:[string, any]) => {
            const category: any = {... value, slug:key};
            category.icon = key;
            const numb = category?.score;
            const matchedInterval = this.indicatorInterval.find(interval => ((interval.min <= numb && numb <= interval.max) && source.eligibleForResult));
            //@ts-ignore
            category.color = matchedInterval ? this.data.colors[matchedInterval.indicatorColor.color] : '#A0ADCE';
            return category;
          });
          return {
            responseRate: source.responseRate,
            score: source.score,
            eligibleForResult: source.eligibleForResult,
            categories,
            scoreStatus: source.score > source.previousScore ? 'increase': source.score < source.previousScore ? 'decrease':'',
            scoreStatusValue: source.score > source.previousScore ? (source.score - source.previousScore): source.score < source.previousScore ? (source.previousScore - source.score):0,
            responseRateStatus: source.responseRate > source.previousResponseRate ? 'increase': source.responseRate < source.previousResponseRate ? 'decrease':''
          }
      })
    )
  }

  getProjectTeamMembersAndFilterThem(): Observable<any> {
    return this.projectService.getProjectTeamMembers(this.projectTeamUuid).pipe(
      map((res: any) => {
        const users = res._embedded.users;
        this.userProjectMembership = users.find((member: any) => member.user.uuid === this.data.userLink.user.uuid) ?? null;
        this.userProjectMembership = {...this.userProjectMembership, workspaceRole: this.data.userLink.user.workspaceRole};
        const members = users.filter((member: any) => member.role === 'member');
        const leaders = users.filter((member: any) => member.role === 'leader');
        return {members: members, leaders: leaders};
      })
    )
  }

  processProjectTeamChartData(): Observable<any> {
    return this.projectService.getProjectTeamChartData(this.projectTeamUuid).pipe(
      map((source: any) => {
        const phases = Array.from(new Map(Object.entries(source)))
          .map(([key, value]:[string, any]) => ({
            phase: {
              title: key,
              chartData: [...value.projectTimedQuestionCollections],
              viewOrder: value.viewOrder
            }
        }));
        return phases;
      })
    );
  }

  getProjectTeamStatus() {
    const currentDay = new Date();
    if (this.projectTeam.status.name === 'active' || this.projectTeam.status.name === 'draft') {
      if (this.projectTeam.kickOffDate) {
        const kickOffDate = new Date(this.projectTeam.kickOffDate.date);
        if (kickOffDate > currentDay) {
          this.projectTeam.status.title = this.translate.instant('pending');
          this.projectTeam.status.color = '#A0ADCE';
        } else if (kickOffDate <= currentDay) {
          this.projectTeam.status.title = this.translate.instant('active');
          this.projectTeam.status.color = '#16C285';
        }
        this.showStatus = true;
      }
      if (this.projectTeam.startDate) {
        const startDate = new Date(this.projectTeam.startDate.date);
        if (startDate > currentDay) {
          this.projectTeam.status.title = this.translate.instant('pending');
          this.projectTeam.status.color = '#A0ADCE';
        } else if (startDate <= currentDay) {
          this.projectTeam.status.title = this.translate.instant('active');
          this.projectTeam.status.color = '#16C285';
        }
        this.showStatus = true;
      }
    } else if (this.projectTeam.status.name === 'inactive' || this.projectTeam.status.name === 'paused') {
        this.projectTeam.status.title = this.translate.instant('team_feedback_status3');
        this.projectTeam.status.color = '#A0ADCE';
        this.showStatus = true;
    } else if (this.projectTeam.status.name === 'completed') {
        this.projectTeam.status.title = this.translate.instant('project_team_dashboard_completed_status');
        this.projectTeam.status.color = '#16C285';
        this.showStatus = true;
    } else if (this.projectTeam.status.name === 'archived') {
        this.projectTeam.status.title = this.translate.instant('project_team_dashboard_edit_archived_status');
        this.projectTeam.status.color = '#A0ADCE';
        this.showStatus = true;
    }
  }

  getProjectTeamDaysRemaining(today: any, deadline: any) {
    const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
    const diffInMs = endDate.getTime() - currentDay.getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    this.projectTeam.remainingDays = diffInDays;
  }

  getProjectTeamScoreColor() {
    const score = this.projectTeam.projectStatistics.score;
    const matchedInterval = this.indicatorInterval.find(interval => (interval.min <= score && score <= interval.max) && this.projectTeam.projectStatistics.eligibleForResult);
    //@ts-ignore
    this.scoreColor = matchedInterval ? this.data.colors[matchedInterval.indicatorColor.color] : '#A0ADCE';
  }

  getResponseRateColor() {
    const responseRate = this.projectTeam.projectStatistics.responseRate;
    const matchedInterval = this.indicatorInterval.find(interval => interval.min <= responseRate && responseRate <= interval.max);
    this.responseRateBackgroundColor = matchedInterval.indicatorColor.color;
  }

  setActiveProjectStatus() {
    const today = new Date();
    const projectTeamStatus = this.projectTeam.status.name;
    const activeProject = this.projectTeam.activeProject;
    const startDate = activeProject.startDate?.date ? new Date(activeProject.startDate.date) : null;
    const endDate = activeProject.endDate?.date ? new Date(activeProject.endDate.date) : null;
    const daysBetween = (from: Date, to: Date): number => Math.max(0, Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)));

    if (endDate && today > endDate) {
      activeProject.isCompleted = true;
      activeProject.isOpen = false;
      activeProject.isPaused = false;
      activeProject.isNotOpen = false;
      activeProject.isArchived = false;
      return;
    }

    if (projectTeamStatus === 'active' || projectTeamStatus === 'draft') {
      if (startDate && endDate && today >= startDate && today <= endDate) {
        const remainingDays = daysBetween(today, endDate);
        activeProject.isCompleted = false;
        activeProject.isOpen = true;
        activeProject.isPaused = false;
        activeProject.isNotOpen = false;
        activeProject.isArchived = false;
        activeProject.status = this.translate.instant('project_team_dashboard_edit_open_status');
        activeProject.remainingDays = this.translate.instant(('project_team_dashboard_edit_open_remaining_day'+(remainingDays !== 1 ? 's':'')), {days: remainingDays});
        return;
      }
      if (startDate) {
        const remainingDays = daysBetween(today, startDate);
        activeProject.status = this.translate.instant(('project_team_dashboard_edit_next_round_day'+(remainingDays !== 1 ? 's':'')), {days: remainingDays});
        activeProject.isCompleted = false;
        activeProject.isOpen = false;
        activeProject.isPaused = false;
        activeProject.isNotOpen = true;
        activeProject.isArchived = false;
        return;
      }
    }

    if (projectTeamStatus === 'inactive' || projectTeamStatus === 'paused') {
      activeProject.status = this.translate.instant('team_feedback_status3');
      activeProject.isCompleted = false;
      activeProject.isOpen = false;
      activeProject.isPaused = true;
      activeProject.isNotOpen = false;
      activeProject.isArchived = false;
      return;
    }

    if (projectTeamStatus === 'archived') {
      activeProject.status = this.translate.instant('project_team_dashboard_edit_archived_status');
      activeProject.isCompleted = false;
      activeProject.isOpen = false;
      activeProject.isPaused = false;
      activeProject.isNotOpen = false;
      activeProject.isArchived = true;
      return;
    }

    if (projectTeamStatus === 'completed') {
      activeProject.status = this.translate.instant('project_team_dashboard_edit_completed_status');
      activeProject.isCompleted = true;
      activeProject.isOpen = false;
      activeProject.isPaused = false;
      activeProject.isNotOpen = false;
      activeProject.isArchived = false;
    }
  }

  getCollectionQuestions(collection: any, index: number): Observable<any> {
    if (collection.loading || collection.questions) {
      return EMPTY;
    }

    collection.loading = true;
    collection.showQuestions = false;
    collection.questions = [];

    return this.projectService.getCollectionQuestions(collection.uuid).pipe(
      map((questions: any) => {
        const statements = questions?._embedded?.["timed-collection-questions"] ?? [];
        return statements.map((statement: any) => {
          const matchedInterval = this.indicatorInterval.find(interval =>
            interval.min <= statement.score &&
            statement.score <= interval.max &&
            statement.eligibleForResult
          );

          return {
            ...statement,
            question: {
              ...statement.question,
              color: matchedInterval ? matchedInterval.indicatorColor.color : 'default',
              rating: Math.round(statement.score / 10),
            }
          };
        });
      }),
      tap((collections: any) => {
        collection.questions = collections;
        collection.loading = false;
        collection.showQuestions = true;
        this.isHighlightScroll = true;
      })
    );
  }

  handleAccordionOpen(collection: any, index: number) {
    if (collection.questions && collection.loading) return;
    requestAnimationFrame(() => {
      this.getCollectionQuestions(collection, index).subscribe();
    });
  }

  selectCollectionTab(index: number) {
    this.showSelectedTabState = this.showSelectedTabState.map(() => false);
    if (this.showSelectedTabCateg[index]) {
      this.showSelectedTabCateg[index] = false;
      return;
    }
    this.showSelectedTabCateg = this.projectTeam.collections.map((_: any, i: number) => i === index);
  }

  emitStatementResponses(statementType: any, statementUuid: any, index: number) {
    this.showSelectedTabState = this.showSelectedTabState.map(() => false);
    this.showSelectedTabState[index] = true;
    this.statementResponses = [];
    if (statementType !== 'ranked') {
      this.projectService.getCollectionQuestionResponses(statementUuid, this.projectTeamUuid, statementType, 1, 6).subscribe((res: any) => {
        if (res && statementType === 'scaled') {
          this.getQuestionDistributionValues(res);
        }
        if (res && statementType === 'open') {
          this.statementResponses = res._embedded.responses;
          this.statementResponseCount = res._total_items;
          this.statementResponsePage = res._page_count;
        }
      });
    }
    // this.statementResponsesEvent.emit({statementType, statementUuid, teamUuid: this.defaultSelectedTeam, page: 1, limit: 6});
  }

  getQuestionDistributionValues(value: any) {
    if (value) {
      value.onInsight = true;
      value.eligibleForResult = true;
      this.responseDistribution = new Observable(subcriber => {
        subcriber.next(value);
      });
    }
  }

  clearStatements() {
    this.statementResponses = [];
  }

  openEditTeam() {
    this.showEditTeam = !this.showEditTeam;
  }

  openEditTeamFromStatus() {
    if (!this.projectTeam.activeProject.isCompleted) {
      this.showEditTeam = !this.showEditTeam;
      this.showEditTeamFromStatus = !this.showEditTeamFromStatus;
      this.showBlinkEffect = !this.showBlinkEffect;
      setTimeout(() => this.showBlinkEffect = !this.showBlinkEffect, 2500);
    }
  }

  openEditDeadlineModal() {
    // @ts-ignore
    document.getElementById('editDeadline').click();
  }

  openConfirmationModal() {
    // @ts-ignore
    document.getElementById('completeConfirmation').click();
  }

  closeConfirmationModal() {
    document.getElementById('closeCompleteConfirmationModal')?.click();
  }

  openArchiveModal() {
    this.modalService.openModal(GenericConfirmModalComponent,
      {
        title: {
          key: 'project_team_dashboard_archive_modal_headline'
        },
        description: {
          key: 'project_team_dashboard_archive_modal_body',
        },
        buttons: [
          {labelKey: 'project_team_dashboard_archive_modal_yes', value: true, className: 'yes-btn'},
          {labelKey: 'change_role_or_permissions_modal_cancel_btn', value: false, className: 'no-btn'}
        ]
      },
      {panelClass: 'generic-modal'}
    )
    .afterClosed()
    .subscribe(result => {
      if (result) {
        this.patchProjectStatus('archived');
      }
    });
  }

  changeProjectDeadline() {
    this.projectService.patchProjectDeadline(this.projectTeamUuid, {endDate: this.datePipe.transform(new Date(this.deadlineModalDate), 'dd.MM.yyyy')}).subscribe((res:any) => {
      document.getElementById('closeEditDeadlineModal')?.click();
      this.projectTeam.endDate = res.endDate;
    });
  }

  patchProjectStatus(status: string) {
    this.projectService.patchProjectStatus(this.projectTeamUuid, {status: status}).subscribe((res:any) => {
      this.projectTeam.status.name = res.status;
      this.getProjectTeamStatus();
      this.setActiveProjectStatus();
    });
  }

  openPauseOrResumeProjectModal(action: string) {
    this.modalService.openModal(GenericConfirmModalComponent,
      {
        title: {
          key: `modal_title_project_team_${action}_project`
        },
        description: {
          key: `modal_description_project_team_${action}_project`,
        },
        buttons: [
          {labelKey: `modal_confirmation_btn_project_team_${action}_project`, value: true, className: 'yes-btn'},
          {labelKey: `modal_cancel_btn_project_team_${action}_project`, value: false, className: 'no-btn'}
        ]
      },
      {panelClass: 'generic-modal'}
    )
    .afterClosed()
    .subscribe(result => {
      if (result && action === 'pause') {
        this.projectService.pauseProject(this.projectTeamUuid).subscribe((res: any) => {
          this.projectTeam.status.name = res.status;
          this.getProjectTeamStatus();
          this.setActiveProjectStatus();
        });
      }

      if(result && action === 'resume') {
        this.projectService.resumeProject(this.projectTeamUuid).subscribe((res: any) => {
          this.projectTeam.status.name = res.status;
          this.getProjectTeamStatus();
          this.setActiveProjectStatus();
        });
      }
    });
  }

  openUpdatePhaseModal() {
    this.projectService.getPhases(this.projectTeam.projectType.uuid).pipe(
      map((res: any) => (res._embedded.phases ?? []).filter((phase: any) => phase.uuid !== this.projectTeam.phase.uuid)),
      switchMap((res: any) => {
        return this.modalService.openModal(UpdatePhaseModalComponent,
          {
            phases: res,
            title: {
              key: 'project_team_dashboard_update_phase_modal_title'
            },
            description: {
              key: 'project_team_dashboard_update_phase_modal_description',
            },
            buttons: [
              {labelKey: 'project_team_dashboard_update_phase_modal_cancel_btn', value: false, className: 'no-btn'},
              {labelKey: 'project_team_dashboard_update_phase_modal_update_btn', value: true, className: 'yes-btn'}
            ]
          },
          { panelClass: ['generic-modal', 'update-phase-modal'],
            width: '88.8rem',
            restoreFocus: false
          }
        ).afterClosed()
      }),
      filter((result: any) => result?.btnValue !== false),
      switchMap(res => this.projectService.updateProjectTeamPhase(this.projectTeamUuid, {startDate: res.phaseData.startDate, projectPhaseUuid: res.phaseData.projectPhaseUuid}))
    ).subscribe(() => {
        this.showDashboard = false;
        this.chartData = [];
        this.projectTeam.projectChartData = [];
        this.projectTeam.projectStatistics = null;
        this.getProjectData();
        this.data.showLoading();
    })
  }

  removeUser(user: any) {
    this.selectedUsers = this.selectedUsers.filter((sUser: any) => sUser.uuid !== user.uuid);
  }

  selectUser(user: any) {
    this.selectedUsers.push(user);
    this.usersSearchControl.setValue('');
    this.pickedUsersForProject = [];
    this.userDropdownVisible = false;
  }

  openAddMoreMembers() {
    this.pickedUsersForProject = [];
    this.usersSearchControl.setValue('');
    this.showUserSearchContainer = !this.showUserSearchContainer;
  }

  closeAddMoreMembers() {
    this.showUserSearchContainer = false;
    this.pickedUsersForProject = [];
    this.selectedUsers = [];
    this.usersSearchControl.setValue('');
  }

  addNewMembersToProject() {
    const membersUuids = this.selectedUsers.map((member: any) => member.user.uuid);
    const membersPayload = {
      projectUuid: this.projectTeamUuid,
      users: membersUuids,
      role: 'member'
    };

    this.projectService.addMembersToProjectTeam(membersPayload, this.projectTeamUuid).pipe(
      concatMap(() =>
        this.projectService.getProjectTeamMembers(this.projectTeamUuid).pipe(
          map((res: any) => res._embedded.users || []),
          map((users: any[]) => users.filter(user => user.role === 'member')),
          map((result: any) => ({projectTeamMembers: result}))
        )
      )
    ).subscribe((res: any) => {
      this.projectTeam.members = res.projectTeamMembers;
      this.closeAddMoreMembers();
    });
  }

  scrollToCheckin(checkin: any) {
    this.isHighlightScroll = false;
    const index = this.projectTeam.collections.findIndex(
      (item: any) => item.uuid === checkin.uuid
    );

    if (index === -1) return;

    if (!this.showSelectedTabCateg[index]) {
      this.selectCollectionTab(index);
    }
    const collection = this.projectTeam.collections[index];
    const div = this.accordionDivs.toArray()[index]?.nativeElement;

    if (collection.showQuestions) {
      div?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    this.getCollectionQuestions(collection, index).subscribe(() => {
      if (this.isHighlightScroll) {
        this.cdRef.detectChanges();
        div?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  openResponsePreviewModal(response: any) {
    this.showedOpenQsResponse = response;
    this.showResponseModal = true;
    const openModal = document.getElementById('actionModalButton');
    openModal?.click();
  }

  openRemoveProjectMemberModal(projectMember: any) {
    this.removeMemberModalOpen = true;
    this.modalService.openModal(GenericConfirmModalComponent,
      {
        title: {
          key: 'modal_title_project_team_remove_member_confirmation_needed'
        },
        description: {
          key: 'modal_title_project_team_remove_member_description',
          params: {
            memberName: (projectMember?.user.detail.firstname+' '+projectMember?.user.detail.lastname),
            projectName: this.projectTeam.name
          }
        },
        buttons: [
          {labelKey: 'remove_them_from_project', value: true, className: 'yes-btn'},
          {labelKey: 'cancel_button', value: false, className: 'no-btn'}
        ]
      },
      {panelClass: 'generic-modal'}
    )
    .afterClosed()
    .subscribe(result => {
      this.removeMemberModalOpen = false;
      if (result) {
        this.projectService.deleteMemberFromProject(this.projectTeamUuid, projectMember.uuid).subscribe(() => {
          this.projectTeam.members = this.projectTeam.members.filter((member: any) => member.uuid !== projectMember.uuid);
        });
      }
    });
  }

  changePage(page: any, statementType: any, statementUuid: any) {
    this.statementResponses = [];
    this.projectService.getCollectionQuestionResponses(statementUuid, this.projectTeamUuid, statementType, page.page + 1, 6).subscribe((res: any) => {
      this.statementResponses = res._embedded.responses;
      this.statementResponseCount = res._total_items;
      this.statementResponsePage = res._page_count;
    });
  }

  copyEmailToClipboard(text: any) {
    navigator.clipboard.writeText(text).then(() => {
      this.data.showAlert(this.translate.instant('copy_email'));
    });
  }

  openCategoryDrillDown(categoryUuid: string) {
    let limit = 4;
    if (window.matchMedia('(min-width: 1920px)').matches) {
      limit = 8;
    } else if (window.matchMedia('(min-width: 1600px)').matches) {
      limit = 6;
    }
    const filters = {page: 1, limit: limit, score: ''};
    const categories = this.projectTeam.projectStatistics.categories.map((item: any) => ({...item}));

    this.projectService.getCategoryDrillDownStatements(this.projectTeamUuid, categoryUuid, filters).pipe(
      map((res: any) => {
        const statements = (res._embedded.project ?? []).map((statement: any) => {
          const matchedInterval = this.indicatorInterval.find(interval =>
            interval.min <= statement.score &&
            statement.score <= interval.max
          );

          return {
            ...statement,
            question: {
              ...statement.question,
              color: matchedInterval ? matchedInterval.indicatorColor.color : 'default',
              rating: Math.round(statement.score / 10),
            }
          };
        });

        return {
          statements,
          currentPage: res._page,
          pageCount: res._page_count,
          totalItems: res._total_items
        };
      })
    ).subscribe((res: any) => {
      this.modalService.openModal(ProjectTeamDrillDownModalComponent,
        {
          projectUuid: this.projectTeamUuid,
          indicatorIntervals: this.indicatorInterval,
          categories: {
            activeCategory: categoryUuid,
            categoriesList: categories
          },
          statements: {
            ...res
          }
        },
        { panelClass: ['generic-modal', 'drill-down-modal'],
          backdropClass: 'drill-down-backdrop',
          width: '80vw',
          maxWidth: '82.5rem',
          restoreFocus: false
        }
      );
    });
  }

  openAddNoteModal(checkin: any) {
    const phase = this.processedPoints
      .map((point: any, index: number) => ({point, index}))
      .find(({point}: any) => point.uuid === checkin.uuid);
    const noteChanges$ = new Subject<string>();

    const modalRef = this.modalService.openModal(ProjectTeamAddNoteModalComponent,
        {
          checkinTitle: checkin.questionCollection.title,
          checkinDescription: checkin.description,
          phaseTitle: phase?.point.phaseTitle,
          phaseColor: this.chartPhaseColors[(phase!.point.phaseViewOrder - 1)].labelBackground,
          editMode: !!checkin.description,
          formData: {
            questionTitleKey: 'project_add_note_question_title',
            questionPlaceholderKey: 'project_add_note_question_placeholder',
            questionSaveBtnKey: 'project_add_note_question_save_btn',
            questionEditBtnKey: 'project_add_note_question_edit_btn',
          },
          noteChanges$
        },
        { panelClass: ['generic-modal', 'drill-down-modal'],
          backdropClass: 'drill-down-backdrop',
          maxWidth: '48.5rem',
          width: '100%',
          restoreFocus: false
        }
      );

      noteChanges$.pipe(
        filter((description: any) => !!description),
        switchMap((description: string) =>
          this.projectService.addProjectCheckinNote(
            this.projectTeamUuid,
            checkin.uuid,
            {description}
          )
          .pipe(
            tap(() => {
              this.projectTeam.collections.find((collection: any) => collection.uuid === checkin.uuid).description = description;
              phase && (phase.point.description = description);
            })
          )
        ),
        takeUntil(modalRef.afterClosed())
      ).subscribe();
  }

  sendReminderToMembers() {
    this.projectService.sendRemindersToProjectMembers(this.projectTeamUuid, this.projectTeam.currentCheckin.uuid)
      .subscribe((res: any) => {
        if (res.error) {
          this.data.showAlertError(res.error.error.error);
          return;
        }
        this.data.showAlert(this.translate.instant('project_team_dashboard_reminder_success'));
      });
  }

  openChangeProjectFrequencyModal(projectFrequency: any) {
    this.projectService.getFrequencies().pipe(
      map((res: any) => (res._embedded.frequencies ?? [])),
      switchMap((res: any) => {
        return this.modalService.openModal(ChangeProjectFrequencyComponent,
          {
            frequencies: res,
            selectedFrequency: projectFrequency.uuid,
            title: {
              key: 'project_team_dashboard_update_frequency_modal_main_title'
            },
            subtitle: {
              key: 'project_team_dashboard_update_frequency_modal_main_subtitle'
            },
            warning: {
              key: 'project_team_dashboard_update_frequency_modal_warning',
            },
            buttons: [
              {labelKey: 'project_team_dashboard_update_frequency_modal_cancel_btn', value: false, className: 'no-btn'},
              {labelKey: 'project_team_dashboard_update_frequency_modal_save_btn', value: true, className: 'yes-btn'}
            ]
          },
          { panelClass: ['generic-modal', 'update-phase-modal'],
            width: '88.8rem',
            restoreFocus: false
          }
        ).afterClosed()
      }),
      filter((result: any) => result?.btnValue !== false),
      switchMap(res => this.projectService.patchProjectFrequency(this.projectTeamUuid, {projectFrequencyUuid: res.projectFrequencyUuid}))
    ).subscribe((res: any) => {
      this.projectTeam.projectFrequency = res.projectFrequency;
    })
  }

  openChangeProjectCheckinStartDateModal() {
    
  }
}
